import type { JSX } from 'react';

import { Button, Icon } from '@bookora/ui';

import { useStepContext } from './context';
import { useTourFeatures } from './features';
import { resolveSteps, tourAllowed } from './resolve-steps';
import { startTour } from './runtime';
import { RUNTIME_COPY } from './runtime-copy';
import { useTourStore } from './tour-store';
import { TOURS } from './tours/registry';
import type { TourKey } from './tours/types';

/**
 * «ببینید چطور کار می‌کند» — a quiet link under an empty state that starts the
 * page's tour at the step about that very thing (`<ShowMeHow tour="crm"
 * step="segments" />` under «هنوز گروهی نساخته‌اید»). For the kit's
 * `EmptyState` `help` slot (the kit cannot import the panel's tours).
 *
 * The press is the person's own, so it spends no proactive budget (source
 * `hub`, like any start from a list of tours) — and it is not held back by
 * `useGuidanceBusy()`, which gates PROACTIVE surfaces: a new owner's welcome
 * stays pending on every setup step's page, which is exactly where these empty
 * states are. Renders nothing when the viewer may not see the tour, the step
 * is not theirs, the tour has no steps yet, or a tour is already running.
 */
export function ShowMeHow({
  tour: key,
  step,
}: {
  tour: TourKey;
  step?: string;
}): JSX.Element | null {
  const ctx = useStepContext();
  const features = useTourFeatures();
  const running = useTourStore((s) => s.active !== null);
  const tour = TOURS[key];
  if (running || !tourAllowed(tour, ctx.permissions, features)) return null;
  const steps = resolveSteps(tour, ctx, { trim: false });
  if (steps.length < tour.minSteps) return null;
  const startAtStepId = step && steps.some((s) => s.id === step) ? step : undefined;
  if (step && !startAtStepId) return null;

  return (
    <Button
      type="link"
      size="small"
      icon={<Icon name="help" size={16} />}
      onClick={(event) =>
        startTour(key, {
          source: 'hub',
          ...(startAtStepId ? { startAtStepId } : {}),
          returnFocusTo: event.currentTarget,
        })
      }
    >
      {RUNTIME_COPY.showMeHow}
    </Button>
  );
}
